// src/components/KioskIntake.jsx
// Full-screen, unattended self-service intake for a reception-desk kiosk/tablet.
// No sidebar, no staff nav, no field-editing — the resulting intake session
// lands in the staff "Intake" page's open list for a receptionist to review
// and turn into a visit.
//
// Touch/text-first, one question per screen: Language -> Name -> Mobile ->
// Age -> Reason -> Documents (optional scan-to-upload from the patient's own
// phone, reusing MobileUploadQRModal against the just-saved intake) ->
// duplicate check -> (existing patient? confirm, then hand off to
// the existing appointment-booking flow) or (new patient? the existing
// receptionist-handoff "thank you" screen). Voice is available at every
// text field as an optional 🎤 assist that fills the field — it never
// drives the flow on its own the way the old guided-Tamil flow did, since
// testing showed voice-first capture wasn't reliable enough to be the
// primary input method (a mis-heard digit or word previously had to be
// caught by ear; now it just lands in an editable field the patient can see
// and correct like anything else).
//
// Duplicate detection and appointment booking reuse the existing
// kiosk-booking.js endpoints/business logic completely unchanged — this
// file only changes how name/mobile/reason get collected beforehand.
import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { useBranding, DEFAULT_ICON_URL } from '../hooks/useBranding';
import { createIntakeSession, updateIntakeSession, authFetch, verifyKioskAccess, kioskVerifyPatient, getKioskBookingDoctors, getKioskBookingSlots, kioskBookSlot } from '../api';
import { useSegmentedRecorder } from '../hooks/useSegmentedRecorder';
import { transcribeSegments } from '../utils/sarvam';
import MobileUploadQRModal, { type MobileSessionStatus } from './MobileUploadQRModal';

type Stage =
  | 'checking' | 'denied' | 'language' | 'name' | 'mobile' | 'age' | 'reason'
  | 'saving' | 'documents'
  | 'checking-match' | 'confirm-identity' | 'booking-offer' | 'booking-doctor'
  | 'booking-slot' | 'booking-confirm' | 'done' | 'error';

type ReasonId = 'consultation' | 'followup' | 'lab' | 'checkup' | 'other';
type MicField = 'name' | 'mobile' | 'age' | 'reasonCustom';

interface MatchedPatient { id: number; name: string; uhid: string }
interface MatchResult {
  matched: boolean;
  patient?: MatchedPatient;
  suggested_doctor_id?: number;
  suggested_doctor_name?: string;
}

interface BookingDoctor {
  id: number;
  full_name: string;
  specialty_name?: string;
}

interface BookingSlot {
  id: number;
  date: string;
  start_time: string;
}

// Every language Sarvam supports for STT (see backend SARVAM_LANGS in
// routes/settings-routes.js) — picking one here switches the whole kiosk UI
// into it, not just the optional voice-input language.
const LANGUAGES = [
  { code:'en-IN', label:'English',    flag:'🇬🇧' },
  { code:'ta-IN', label:'தமிழ்',      flag:'🇮🇳' },
  { code:'hi-IN', label:'हिंदी',       flag:'🇮🇳' },
  { code:'te-IN', label:'తెలుగు',     flag:'🇮🇳' },
  { code:'kn-IN', label:'ಕನ್ನಡ',      flag:'🇮🇳' },
  { code:'ml-IN', label:'മലയാളം',    flag:'🇮🇳' },
  { code:'mr-IN', label:'मराठी',       flag:'🇮🇳' },
  { code:'bn-IN', label:'বাংলা',       flag:'🇮🇳' },
  { code:'gu-IN', label:'ગુજરાતી',    flag:'🇮🇳' },
];

const REASONS: ReasonId[] = ['consultation', 'followup', 'lab', 'checkup', 'other'];
const REASON_ICON: Record<ReasonId, string> = { consultation:'🩺', followup:'🔁', lab:'🧪', checkup:'❤️‍🩹', other:'✏️' };
// Canonical English label stored on the intake record regardless of which
// language the patient tapped it in — keeps reception's review list
// consistent no matter what language a given kiosk session ran in.
const REASON_LABEL_EN: Record<Exclude<ReasonId, 'other'>, string> = { consultation:'Consultation', followup:'Follow-up', lab:'Lab Test', checkup:'Health Check-up' };

const STRINGS = {
  'en-IN': {
    chooseLanguage:'Choose your language',
    nameTitle:'What is your name?', namePlaceholder:'Type your name',
    mobileTitle:'What is your mobile number?', mobilePlaceholder:'Mobile number', mobileHintInvalid:'Mobile numbers should have 10 digits',
    ageTitle:"How old are you?", agePlaceholder:"Age in years", ageHintInvalid:"Please enter an age between 1 and 120",
    docsTitle:"Do you have any medical documents?", docsSubtitle:"Previous prescriptions, lab reports or scans — upload photos from your phone. This is optional.",
    docsScan:"Scan to upload from phone", docsQrHint:"Scan this with your phone's camera, then take photos or choose files to upload.", docsReceived:"documents received", docsSkip:"Skip",
    reasonTitle:'Why are you visiting today?', reasonOtherPlaceholder:'Please describe',
    reasonConsultation:'Consultation', reasonFollowup:'Follow-up', reasonLab:'Lab Test', reasonCheckup:'Health Check-up', reasonOther:'Other',
    back:'Back', next:'Continue',
    checkingRecords:'Checking your records…',
    isThisYou:'Is this you?', yesThatsMe:"Yes, that's me", noSomeoneElse:"No, that's not me",
    thankYou:'Thank you!', doneMessage:'Your details have been recorded. Please take a seat — our receptionist will call you shortly.', reference:'Reference',
    micTap:'Tap to speak', micListening:'Listening… tap to stop', micProcessing:'Processing…',
    micDenied:'Microphone access denied — please type instead', micError:'Could not hear you — please try again or type instead',
    startOver:'Start over', errorGeneric:'Something went wrong. Please ask reception for help.',
  },
  'ta-IN': {
    chooseLanguage:'உங்கள் மொழியைத் தேர்ந்தெடுக்கவும்',
    nameTitle:'உங்கள் பெயர் என்ன?', namePlaceholder:'உங்கள் பெயரை உள்ளிடவும்',
    mobileTitle:'உங்கள் மொபைல் நம்பர் என்ன?', mobilePlaceholder:'மொபைல் நம்பர்', mobileHintInvalid:'மொபைல் எண் 10 இலக்கங்களாக இருக்க வேண்டும்',
    ageTitle:"உங்கள் வயது என்ன?", agePlaceholder:"வயது (ஆண்டுகளில்)", ageHintInvalid:"தயவுசெய்து 1 முதல் 120 வரை வயதை உள்ளிடவும்",
    docsTitle:"உங்களிடம் மருத்துவ ஆவணங்கள் ஏதேனும் உள்ளதா?", docsSubtitle:"முந்தைய மருந்துச் சீட்டுகள், ஆய்வக அறிக்கைகள் அல்லது ஸ்கேன்கள் — உங்கள் மொபைலில் இருந்து புகைப்படங்களைப் பதிவேற்றவும். இது விருப்பத்திற்குரியது.",
    docsScan:"மொபைல் மூலம் பதிவேற்ற ஸ்கேன் செய்யவும்", docsQrHint:"உங்கள் மொபைல் கேமராவால் இதை ஸ்கேன் செய்து, புகைப்படங்கள் எடுக்கவும் அல்லது கோப்புகளைத் தேர்ந்தெடுக்கவும்.", docsReceived:"ஆவணங்கள் பெறப்பட்டன", docsSkip:"தவிர்",
    reasonTitle:'இன்று ஏன் வருகை தந்துள்ளீர்கள்?', reasonOtherPlaceholder:'தயவுசெய்து விவரிக்கவும்',
    reasonConsultation:'ஆலோசனை', reasonFollowup:'பின்தொடர்தல்', reasonLab:'ஆய்வக பரிசோதனை', reasonCheckup:'உடல்நல பரிசோதனை', reasonOther:'மற்றவை',
    back:'பின்செல்', next:'தொடரவும்',
    checkingRecords:'உங்கள் பதிவுகளை சரிபார்க்கிறோம்…',
    isThisYou:'இது நீங்களா?', yesThatsMe:'ஆம், இது நான்தான்', noSomeoneElse:'இல்லை, இது நான் இல்லை',
    thankYou:'நன்றி!', doneMessage:'உங்கள் விவரங்கள் பதிவு செய்யப்பட்டன. தயவுசெய்து அமரவும் — எங்கள் ரிசெப்ஷனிஸ்ட் விரைவில் உங்களை அழைப்பார்கள்.', reference:'குறிப்பு எண்',
    micTap:'பேச தட்டவும்', micListening:'கேட்கிறோம்… நிறுத்த தட்டவும்', micProcessing:'செயலாக்குகிறது…',
    micDenied:'மைக்ரோஃபோன் அணுகல் மறுக்கப்பட்டது — தயவுசெய்து தட்டச்சு செய்யவும்', micError:'கேட்கவில்லை — மீண்டும் முயற்சிக்கவும் அல்லது தட்டச்சு செய்யவும்',
    startOver:'மீண்டும் தொடங்கு', errorGeneric:'ஏதோ தவறு நடந்துவிட்டது. தயவுசெய்து ரிசெப்ஷனிடம் உதவி கேளுங்கள்.',
  },
  'hi-IN': {
    chooseLanguage:'अपनी भाषा चुनें',
    nameTitle:'आपका नाम क्या है?', namePlaceholder:'अपना नाम लिखें',
    mobileTitle:'आपका मोबाइल नंबर क्या है?', mobilePlaceholder:'मोबाइल नंबर', mobileHintInvalid:'मोबाइल नंबर 10 अंकों का होना चाहिए',
    ageTitle:"आपकी उम्र क्या है?", agePlaceholder:"उम्र (वर्षों में)", ageHintInvalid:"कृपया 1 से 120 के बीच उम्र दर्ज करें",
    docsTitle:"क्या आपके पास कोई मेडिकल दस्तावेज़ हैं?", docsSubtitle:"पिछले पर्चे, लैब रिपोर्ट या स्कैन — अपने फ़ोन से फ़ोटो अपलोड करें। यह वैकल्पिक है।",
    docsScan:"फ़ोन से अपलोड करने के लिए स्कैन करें", docsQrHint:"इसे अपने फ़ोन के कैमरे से स्कैन करें, फिर फ़ोटो लें या फ़ाइलें चुनें।", docsReceived:"दस्तावेज़ प्राप्त हुए", docsSkip:"छोड़ें",
    reasonTitle:'आज आप क्यों आए हैं?', reasonOtherPlaceholder:'कृपया बताएं',
    reasonConsultation:'परामर्श', reasonFollowup:'फॉलो-अप', reasonLab:'लैब टेस्ट', reasonCheckup:'स्वास्थ्य जांच', reasonOther:'अन्य',
    back:'वापस', next:'आगे बढ़ें',
    checkingRecords:'आपका रिकॉर्ड जांचा जा रहा है…',
    isThisYou:'क्या यह आप हैं?', yesThatsMe:'हाँ, यह मैं हूँ', noSomeoneElse:'नहीं, यह मैं नहीं हूँ',
    thankYou:'धन्यवाद!', doneMessage:'आपकी जानकारी दर्ज कर ली गई है। कृपया बैठें — हमारा रिसेप्शनिस्ट जल्द ही आपको बुलाएगा।', reference:'संदर्भ संख्या',
    micTap:'बोलने के लिए टैप करें', micListening:'सुन रहे हैं… रोकने के लिए टैप करें', micProcessing:'प्रोसेस हो रहा है…',
    micDenied:'माइक्रोफ़ोन एक्सेस अस्वीकृत — कृपया टाइप करें', micError:'सुनाई नहीं दिया — कृपया फिर से कोशिश करें या टाइप करें',
    startOver:'फिर से शुरू करें', errorGeneric:'कुछ गलत हो गया। कृपया रिसेप्शन से मदद लें।',
  },
  'te-IN': {
    chooseLanguage:'మీ భాషను ఎంచుకోండి',
    nameTitle:'మీ పేరు ఏమిటి?', namePlaceholder:'మీ పేరు టైప్ చేయండి',
    mobileTitle:'మీ మొబైల్ నంబర్ ఏమిటి?', mobilePlaceholder:'మొబైల్ నంబర్', mobileHintInvalid:'మొబైల్ నంబర్ 10 అంకెలు ఉండాలి',
    ageTitle:"మీ వయస్సు ఎంత?", agePlaceholder:"వయస్సు (సంవత్సరాలలో)", ageHintInvalid:"దయచేసి 1 నుండి 120 మధ్య వయస్సు నమోదు చేయండి",
    docsTitle:"మీ వద్ద ఏవైనా వైద్య పత్రాలు ఉన్నాయా?", docsSubtitle:"మునుపటి ప్రిస్క్రిప్షన్లు, ల్యాబ్ రిపోర్టులు లేదా స్కాన్లు — మీ ఫోన్ నుండి ఫోటోలు అప్‌లోడ్ చేయండి. ఇది ఐచ్ఛికం.",
    docsScan:"ఫోన్ నుండి అప్‌లోడ్ చేయడానికి స్కాన్ చేయండి", docsQrHint:"మీ ఫోన్ కెమెరాతో దీన్ని స్కాన్ చేసి, ఫోటోలు తీయండి లేదా ఫైళ్లను ఎంచుకోండి.", docsReceived:"పత్రాలు అందాయి", docsSkip:"దాటవేయి",
    reasonTitle:'ఈరోజు మీరు ఎందుకు వచ్చారు?', reasonOtherPlaceholder:'దయచేసి వివరించండి',
    reasonConsultation:'సంప్రదింపు', reasonFollowup:'ఫాలో-అప్', reasonLab:'ల్యాబ్ టెస్ట్', reasonCheckup:'ఆరోగ్య పరీక్ష', reasonOther:'ఇతర',
    back:'వెనక్కి', next:'కొనసాగించు',
    checkingRecords:'మీ రికార్డులను తనిఖీ చేస్తున్నాము…',
    isThisYou:'ఇది మీరేనా?', yesThatsMe:'అవును, ఇది నేనే', noSomeoneElse:'లేదు, ఇది నేను కాదు',
    thankYou:'ధన్యవాదాలు!', doneMessage:'మీ వివరాలు నమోదు చేయబడ్డాయి. దయచేసి కూర్చోండి — మా రిసెప్షనిస్ట్ త్వరలో మిమ్మల్ని పిలుస్తారు.', reference:'సూచన సంఖ్య',
    micTap:'మాట్లాడటానికి నొక్కండి', micListening:'వింటున్నాము… ఆపడానికి నొక్కండి', micProcessing:'ప్రాసెస్ చేస్తోంది…',
    micDenied:'మైక్రోఫోన్ యాక్సెస్ నిరాకరించబడింది — దయచేసి టైప్ చేయండి', micError:'వినిపించలేదు — దయచేసి మళ్ళీ ప్రయత్నించండి లేదా టైప్ చేయండి',
    startOver:'మళ్ళీ ప్రారంభించండి', errorGeneric:'ఏదో తప్పు జరిగింది. దయచేసి రిసెప్షన్ సహాయం తీసుకోండి.',
  },
  'kn-IN': {
    chooseLanguage:'ನಿಮ್ಮ ಭಾಷೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ',
    nameTitle:'ನಿಮ್ಮ ಹೆಸರೇನು?', namePlaceholder:'ನಿಮ್ಮ ಹೆಸರನ್ನು ಟೈಪ್ ಮಾಡಿ',
    mobileTitle:'ನಿಮ್ಮ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ಏನು?', mobilePlaceholder:'ಮೊಬೈಲ್ ಸಂಖ್ಯೆ', mobileHintInvalid:'ಮೊಬೈಲ್ ಸಂಖ್ಯೆ 10 ಅಂಕಿಗಳಿರಬೇಕು',
    ageTitle:"ನಿಮ್ಮ ವಯಸ್ಸು ಎಷ್ಟು?", agePlaceholder:"ವಯಸ್ಸು (ವರ್ಷಗಳಲ್ಲಿ)", ageHintInvalid:"ದಯವಿಟ್ಟು 1 ರಿಂದ 120 ರ ನಡುವಿನ ವಯಸ್ಸನ್ನು ನಮೂದಿಸಿ",
    docsTitle:"ನಿಮ್ಮ ಬಳಿ ಯಾವುದೇ ವೈದ್ಯಕೀಯ ದಾಖಲೆಗಳಿವೆಯೇ?", docsSubtitle:"ಹಿಂದಿನ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್‌ಗಳು, ಲ್ಯಾಬ್ ವರದಿಗಳು ಅಥವಾ ಸ್ಕ್ಯಾನ್‌ಗಳು — ನಿಮ್ಮ ಫೋನ್‌ನಿಂದ ಫೋಟೋಗಳನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ. ಇದು ಐಚ್ಛಿಕ.",
    docsScan:"ಫೋನ್‌ನಿಂದ ಅಪ್‌ಲೋಡ್ ಮಾಡಲು ಸ್ಕ್ಯಾನ್ ಮಾಡಿ", docsQrHint:"ನಿಮ್ಮ ಫೋನ್ ಕ್ಯಾಮೆರಾದಿಂದ ಇದನ್ನು ಸ್ಕ್ಯಾನ್ ಮಾಡಿ, ನಂತರ ಫೋಟೋ ತೆಗೆಯಿರಿ ಅಥವಾ ಫೈಲ್‌ಗಳನ್ನು ಆಯ್ಕೆಮಾಡಿ.", docsReceived:"ದಾಖಲೆಗಳು ಸ್ವೀಕರಿಸಲಾಗಿದೆ", docsSkip:"ಬಿಟ್ಟುಬಿಡಿ",
    reasonTitle:'ಇಂದು ನೀವು ಏಕೆ ಬಂದಿದ್ದೀರಿ?', reasonOtherPlaceholder:'ದಯವಿಟ್ಟು ವಿವರಿಸಿ',
    reasonConsultation:'ಸಮಾಲೋಚನೆ', reasonFollowup:'ಫಾಲೋ-ಅಪ್', reasonLab:'ಲ್ಯಾಬ್ ಪರೀಕ್ಷೆ', reasonCheckup:'ಆರೋಗ್ಯ ತಪಾಸಣೆ', reasonOther:'ಇತರೆ',
    back:'ಹಿಂದೆ', next:'ಮುಂದುವರಿಸಿ',
    checkingRecords:'ನಿಮ್ಮ ದಾಖಲೆಗಳನ್ನು ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ…',
    isThisYou:'ಇದು ನೀವೇ?', yesThatsMe:'ಹೌದು, ಇದು ನಾನೇ', noSomeoneElse:'ಇಲ್ಲ, ಇದು ನಾನಲ್ಲ',
    thankYou:'ಧನ್ಯವಾದಗಳು!', doneMessage:'ನಿಮ್ಮ ವಿವರಗಳನ್ನು ದಾಖಲಿಸಲಾಗಿದೆ. ದಯವಿಟ್ಟು ಕುಳಿತುಕೊಳ್ಳಿ — ನಮ್ಮ ರಿಸೆಪ್ಷನಿಸ್ಟ್ ಶೀಘ್ರದಲ್ಲೇ ನಿಮ್ಮನ್ನು ಕರೆಯುತ್ತಾರೆ.', reference:'ಉಲ್ಲೇಖ ಸಂಖ್ಯೆ',
    micTap:'ಮಾತನಾಡಲು ಟ್ಯಾಪ್ ಮಾಡಿ', micListening:'ಕೇಳುತ್ತಿದ್ದೇವೆ… ನಿಲ್ಲಿಸಲು ಟ್ಯಾಪ್ ಮಾಡಿ', micProcessing:'ಪ್ರಕ್ರಿಯೆಗೊಳಿಸಲಾಗುತ್ತಿದೆ…',
    micDenied:'ಮೈಕ್ರೊಫೋನ್ ಪ್ರವೇಶ ನಿರಾಕರಿಸಲಾಗಿದೆ — ದಯವಿಟ್ಟು ಟೈಪ್ ಮಾಡಿ', micError:'ಕೇಳಿಸಲಿಲ್ಲ — ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ ಅಥವಾ ಟೈಪ್ ಮಾಡಿ',
    startOver:'ಮತ್ತೆ ಪ್ರಾರಂಭಿಸಿ', errorGeneric:'ಏನೋ ತಪ್ಪಾಗಿದೆ. ದಯವಿಟ್ಟು ರಿಸೆಪ್ಷನ್ ಸಹಾಯ ಪಡೆಯಿರಿ.',
  },
  'ml-IN': {
    chooseLanguage:'നിങ്ങളുടെ ഭാഷ തിരഞ്ഞെടുക്കുക',
    nameTitle:'നിങ്ങളുടെ പേര് എന്താണ്?', namePlaceholder:'നിങ്ങളുടെ പേര് ടൈപ്പ് ചെയ്യുക',
    mobileTitle:'നിങ്ങളുടെ മൊബൈൽ നമ്പർ എന്താണ്?', mobilePlaceholder:'മൊബൈൽ നമ്പർ', mobileHintInvalid:'മൊബൈൽ നമ്പർ 10 അക്കങ്ങൾ ആയിരിക്കണം',
    ageTitle:"നിങ്ങളുടെ പ്രായം എത്ര?", agePlaceholder:"പ്രായം (വർഷങ്ങളിൽ)", ageHintInvalid:"ദയവായി 1 മുതൽ 120 വരെയുള്ള പ്രായം നൽകുക",
    docsTitle:"നിങ്ങളുടെ കൈവശം എന്തെങ്കിലും മെഡിക്കൽ രേഖകൾ ഉണ്ടോ?", docsSubtitle:"മുൻ കുറിപ്പടികൾ, ലാബ് റിപ്പോർട്ടുകൾ അല്ലെങ്കിൽ സ്കാനുകൾ — നിങ്ങളുടെ ഫോണിൽ നിന്ന് ഫോട്ടോകൾ അപ്‌ലോഡ് ചെയ്യുക. ഇത് നിർബന്ധമല്ല.",
    docsScan:"ഫോണിൽ നിന്ന് അപ്‌ലോഡ് ചെയ്യാൻ സ്കാൻ ചെയ്യുക", docsQrHint:"നിങ്ങളുടെ ഫോൺ ക്യാമറ ഉപയോഗിച്ച് ഇത് സ്കാൻ ചെയ്ത്, ഫോട്ടോ എടുക്കുക അല്ലെങ്കിൽ ഫയലുകൾ തിരഞ്ഞെടുക്കുക.", docsReceived:"രേഖകൾ ലഭിച്ചു", docsSkip:"ഒഴിവാക്കുക",
    reasonTitle:'ഇന്ന് നിങ്ങൾ എന്തിനാണ് വന്നത്?', reasonOtherPlaceholder:'ദയവായി വിവരിക്കുക',
    reasonConsultation:'കൺസൾട്ടേഷൻ', reasonFollowup:'ഫോളോ-അപ്പ്', reasonLab:'ലാബ് ടെസ്റ്റ്', reasonCheckup:'ആരോഗ്യ പരിശോധന', reasonOther:'മറ്റുള്ളവ',
    back:'തിരികെ', next:'തുടരുക',
    checkingRecords:'നിങ്ങളുടെ രേഖകൾ പരിശോധിക്കുന്നു…',
    isThisYou:'ഇത് നിങ്ങളാണോ?', yesThatsMe:'അതെ, ഇത് ഞാൻ തന്നെ', noSomeoneElse:'അല്ല, ഇത് ഞാൻ അല്ല',
    thankYou:'നന്ദി!', doneMessage:'നിങ്ങളുടെ വിവരങ്ങൾ രേഖപ്പെടുത്തി. ദയവായി ഇരിക്കുക — ഞങ്ങളുടെ റിസപ്ഷനിസ്റ്റ് ഉടൻ നിങ്ങളെ വിളിക്കും.', reference:'റഫറൻസ് നമ്പർ',
    micTap:'സംസാരിക്കാൻ ടാപ്പ് ചെയ്യുക', micListening:'കേൾക്കുന്നു… നിർത്താൻ ടാപ്പ് ചെയ്യുക', micProcessing:'പ്രോസസ്സ് ചെയ്യുന്നു…',
    micDenied:'മൈക്രോഫോൺ ആക്സസ് നിഷേധിച്ചു — ദയവായി ടൈപ്പ് ചെയ്യുക', micError:'കേട്ടില്ല — വീണ്ടും ശ്രമിക്കുക അല്ലെങ്കിൽ ടൈപ്പ് ചെയ്യുക',
    startOver:'വീണ്ടും ആരംഭിക്കുക', errorGeneric:'എന്തോ പിശക് സംഭവിച്ചു. ദയവായി റിസപ്ഷനിൽ സഹായം ചോദിക്കുക.',
  },
  'mr-IN': {
    chooseLanguage:'तुमची भाषा निवडा',
    nameTitle:'तुमचे नाव काय आहे?', namePlaceholder:'तुमचे नाव टाइप करा',
    mobileTitle:'तुमचा मोबाईल नंबर काय आहे?', mobilePlaceholder:'मोबाईल नंबर', mobileHintInvalid:'मोबाईल नंबर 10 अंकी असावा',
    ageTitle:"तुमचे वय किती आहे?", agePlaceholder:"वय (वर्षांमध्ये)", ageHintInvalid:"कृपया 1 ते 120 दरम्यान वय टाका",
    docsTitle:"तुमच्याकडे काही वैद्यकीय कागदपत्रे आहेत का?", docsSubtitle:"मागील प्रिस्क्रिप्शन, लॅब रिपोर्ट किंवा स्कॅन — तुमच्या फोनवरून फोटो अपलोड करा. हे ऐच्छिक आहे.",
    docsScan:"फोनवरून अपलोड करण्यासाठी स्कॅन करा", docsQrHint:"तुमच्या फोनच्या कॅमेऱ्याने हे स्कॅन करा, नंतर फोटो काढा किंवा फाइल्स निवडा.", docsReceived:"कागदपत्रे मिळाली", docsSkip:"वगळा",
    reasonTitle:'आज तुम्ही का आला आहात?', reasonOtherPlaceholder:'कृपया सांगा',
    reasonConsultation:'सल्लामसलत', reasonFollowup:'फॉलो-अप', reasonLab:'लॅब टेस्ट', reasonCheckup:'आरोग्य तपासणी', reasonOther:'इतर',
    back:'मागे', next:'पुढे जा',
    checkingRecords:'तुमची नोंद तपासत आहोत…',
    isThisYou:'हे तुम्ही आहात का?', yesThatsMe:'होय, हा मीच आहे', noSomeoneElse:'नाही, हा मी नाही',
    thankYou:'धन्यवाद!', doneMessage:'तुमची माहिती नोंदवली गेली आहे. कृपया बसा — आमचा रिसेप्शनिस्ट लवकरच तुम्हाला बोलावेल.', reference:'संदर्भ क्रमांक',
    micTap:'बोलण्यासाठी टॅप करा', micListening:'ऐकत आहोत… थांबवण्यासाठी टॅप करा', micProcessing:'प्रक्रिया करत आहे…',
    micDenied:'मायक्रोफोन प्रवेश नाकारला — कृपया टाइप करा', micError:'ऐकू आले नाही — कृपया पुन्हा प्रयत्न करा किंवा टाइप करा',
    startOver:'पुन्हा सुरू करा', errorGeneric:'काहीतरी चूक झाली. कृपया रिसेप्शनकडून मदत घ्या.',
  },
  'bn-IN': {
    chooseLanguage:'আপনার ভাষা নির্বাচন করুন',
    nameTitle:'আপনার নাম কী?', namePlaceholder:'আপনার নাম টাইপ করুন',
    mobileTitle:'আপনার মোবাইল নম্বর কী?', mobilePlaceholder:'মোবাইল নম্বর', mobileHintInvalid:'মোবাইল নম্বর ১০ সংখ্যার হওয়া উচিত',
    ageTitle:"আপনার বয়স কত?", agePlaceholder:"বয়স (বছরে)", ageHintInvalid:"অনুগ্রহ করে ১ থেকে ১২০-এর মধ্যে বয়স লিখুন",
    docsTitle:"আপনার কাছে কি কোনো মেডিকেল নথি আছে?", docsSubtitle:"আগের প্রেসক্রিপশন, ল্যাব রিপোর্ট বা স্ক্যান — আপনার ফোন থেকে ছবি আপলোড করুন। এটি ঐচ্ছিক।",
    docsScan:"ফোন থেকে আপলোড করতে স্ক্যান করুন", docsQrHint:"আপনার ফোনের ক্যামেরা দিয়ে এটি স্ক্যান করুন, তারপর ছবি তুলুন বা ফাইল বেছে নিন।", docsReceived:"নথি পাওয়া গেছে", docsSkip:"এড়িয়ে যান",
    reasonTitle:'আজ আপনি কেন এসেছেন?', reasonOtherPlaceholder:'অনুগ্রহ করে বর্ণনা করুন',
    reasonConsultation:'পরামর্শ', reasonFollowup:'ফলো-আপ', reasonLab:'ল্যাব টেস্ট', reasonCheckup:'স্বাস্থ্য পরীক্ষা', reasonOther:'অন্যান্য',
    back:'পিছনে', next:'চালিয়ে যান',
    checkingRecords:'আপনার রেকর্ড পরীক্ষা করা হচ্ছে…',
    isThisYou:'এটা কি আপনি?', yesThatsMe:'হ্যাঁ, এটা আমি', noSomeoneElse:'না, এটা আমি নই',
    thankYou:'ধন্যবাদ!', doneMessage:'আপনার তথ্য নথিভুক্ত করা হয়েছে। অনুগ্রহ করে বসুন — আমাদের রিসেপশনিস্ট শীঘ্রই আপনাকে ডাকবেন।', reference:'রেফারেন্স নম্বর',
    micTap:'কথা বলতে ট্যাপ করুন', micListening:'শুনছি… থামাতে ট্যাপ করুন', micProcessing:'প্রক্রিয়াকরণ হচ্ছে…',
    micDenied:'মাইক্রোফোন অ্যাক্সেস প্রত্যাখ্যাত — অনুগ্রহ করে টাইপ করুন', micError:'শোনা যায়নি — আবার চেষ্টা করুন বা টাইপ করুন',
    startOver:'আবার শুরু করুন', errorGeneric:'কিছু ভুল হয়েছে। অনুগ্রহ করে রিসেপশনের সাহায্য নিন।',
  },
  'gu-IN': {
    chooseLanguage:'તમારી ભાષા પસંદ કરો',
    nameTitle:'તમારું નામ શું છે?', namePlaceholder:'તમારું નામ ટાઇપ કરો',
    mobileTitle:'તમારો મોબાઇલ નંબર શું છે?', mobilePlaceholder:'મોબાઇલ નંબર', mobileHintInvalid:'મોબાઇલ નંબર 10 અંકનો હોવો જોઈએ',
    ageTitle:"તમારી ઉંમર કેટલી છે?", agePlaceholder:"ઉંમર (વર્ષમાં)", ageHintInvalid:"કૃપા કરીને 1 થી 120 વચ્ચેની ઉંમર દાખલ કરો",
    docsTitle:"શું તમારી પાસે કોઈ તબીબી દસ્તાવેજો છે?", docsSubtitle:"અગાઉના પ્રિસ્ક્રિપ્શન, લેબ રિપોર્ટ અથવા સ્કેન — તમારા ફોનમાંથી ફોટા અપલોડ કરો. આ વૈકલ્પિક છે.",
    docsScan:"ફોનથી અપલોડ કરવા માટે સ્કેન કરો", docsQrHint:"તમારા ફોનના કેમેરાથી આ સ્કેન કરો, પછી ફોટા લો અથવા ફાઇલો પસંદ કરો.", docsReceived:"દસ્તાવેજો મળ્યા", docsSkip:"છોડો",
    reasonTitle:'આજે તમે શા માટે આવ્યા છો?', reasonOtherPlaceholder:'કૃપા કરીને વર્ણન કરો',
    reasonConsultation:'પરામર્શ', reasonFollowup:'ફોલો-અપ', reasonLab:'લેબ ટેસ્ટ', reasonCheckup:'આરોગ્ય તપાસ', reasonOther:'અન્ય',
    back:'પાછળ', next:'આગળ વધો',
    checkingRecords:'તમારો રેકોર્ડ તપાસી રહ્યા છીએ…',
    isThisYou:'શું આ તમે છો?', yesThatsMe:'હા, આ હું છું', noSomeoneElse:'ના, આ હું નથી',
    thankYou:'આભાર!', doneMessage:'તમારી વિગતો નોંધવામાં આવી છે. કૃપા કરીને બેસો — અમારા રિસેપ્શનિસ્ટ ટૂંક સમયમાં તમને બોલાવશે.', reference:'સંદર્ભ નંબર',
    micTap:'બોલવા માટે ટેપ કરો', micListening:'સાંભળી રહ્યા છીએ… રોકવા માટે ટેપ કરો', micProcessing:'પ્રક્રિયા થઈ રહી છે…',
    micDenied:'માઇક્રોફોન ઍક્સેસ નકારવામાં આવ્યો — કૃપા કરીને ટાઇપ કરો', micError:'સાંભળાયું નહીં — ફરી પ્રયાસ કરો અથવા ટાઇપ કરો',
    startOver:'ફરીથી શરૂ કરો', errorGeneric:'કંઈક ખોટું થયું. કૃપા કરીને રિસેપ્શન પાસેથી મદદ લો.',
  },
};
type StringKey = keyof typeof STRINGS['en-IN'];
const t = (lang: string, key: StringKey): string =>
  (STRINGS as Record<string, Record<string, string>>)[lang]?.[key] ?? STRINGS['en-IN'][key];

// STT engines transcribe spoken digit sequences as literal numerals almost
// universally, regardless of the spoken language — strip everything else
// and take the last 10 digits (guards against a leading "+91" or a stray
// extra digit).
function extractMobileDigits(text: string) {
  const digits = (text || '').match(/\d/g) || [];
  return digits.join('').slice(-10);
}

// First number spoken ("I am 42 years old" -> "42"), capped to 3 digits.
function extractAge(text: string) {
  return ((text || '').match(/\d+/)?.[0] || '').slice(0, 3);
}
const isValidAge = (age: string) => /^\d{1,3}$/.test(age) && Number(age) >= 1 && Number(age) <= 120;

const RESET_AFTER_MS = 15000;

export default function KioskIntake() {
  const { branding } = useBranding();
  // Anyone who finds the ?kiosk=1 URL (without the ?token=... a staff member
  // fetched for the actual kiosk tablet) would otherwise see a fully working
  // intake flow even though every API call it makes fails with 401 — the
  // buttons "work" but nothing gets saved, which is confusing rather than
  // actually protected. 'checking'/'denied' gate the UI itself on the same
  // kiosk token the backend already enforces, verified once on load.
  const [stage, setStage] = useState<Stage>('checking');
  const [lang, setLang] = useState('en-IN');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [age, setAge] = useState('');
  const [reason, setReason] = useState<ReasonId | null>(null);
  const [reasonCustom, setReasonCustom] = useState('');
  const [matchResult, setMatchResult] = useState<MatchResult | null>(null); // kioskVerifyPatient() result once checked
  const [reference, setReference] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [showUploadQR, setShowUploadQR] = useState(false);
  const [docsReceived, setDocsReceived] = useState(0);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { stopAndWait, start: startRec } = useSegmentedRecorder();
  const [micField, setMicField] = useState<MicField | null>(null); // currently recording
  const [micProcessingField, setMicProcessingField] = useState<MicField | null>(null); // field currently being transcribed, or null
  const [micErr, setMicErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!new URLSearchParams(window.location.search).get('token')) { setStage('denied'); return; }
      try {
        await verifyKioskAccess();
        if (!cancelled) setStage('language');
      } catch {
        if (!cancelled) setStage('denied');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Self-service appointment booking — unchanged from the existing flow.
  // bookingOffer is the same shape kioskVerifyPatient() returns; entering
  // it from the new confirm-identity screen ("Yes, that's me") instead of
  // the old guided flow's auto-advance is the only thing that changed.
  const [bookingOffer, setBookingOffer] = useState<MatchResult | null>(null);
  const [bookingDoctors, setBookingDoctors] = useState<BookingDoctor[]>([]);
  const [bookingSelectedDoctor, setBookingSelectedDoctor] = useState<BookingDoctor | null>(null);
  const [bookingSlots, setBookingSlots] = useState<BookingSlot[] | null>(null);
  const [bookingError, setBookingError] = useState('');
  const [bookingConfirmed, setBookingConfirmed] = useState<BookingSlot | null>(null);

  useEffect(() => () => { clearTimeout(resetTimerRef.current); }, []);

  const goStart = () => {
    clearTimeout(resetTimerRef.current);
    setStage('language'); setLang('en-IN');
    setName(''); setMobile(''); setAge(''); setReason(null); setReasonCustom('');
    setMatchResult(null); setReference(null); setErrorMsg('');
    setShowUploadQR(false); setDocsReceived(0);
    setMicField(null); setMicErr('');
    setBookingOffer(null); setBookingDoctors([]); setBookingSelectedDoctor(null);
    setBookingSlots(null); setBookingError(''); setBookingConfirmed(null);
  };

  // ── Optional per-field voice assist ───────────────────────────────
  // Tap to start, tap again to stop — fills whichever field is currently
  // being dictated. Never auto-advances the flow; the patient can see and
  // edit whatever lands in the field before continuing, same as if they'd
  // typed it themselves.
  const startMic = async (field: MicField) => {
    setMicErr('');
    try { await startRec(); setMicField(field); }
    catch { setMicErr(t(lang, 'micDenied')); }
  };
  const stopMic = async () => {
    const field = micField;
    setMicField(null); setMicProcessingField(field);
    const segments = await stopAndWait();
    try {
      const transcript = await transcribeSegments(segments, lang, 'kiosk');
      if (!transcript || !transcript.trim()) throw new Error('empty');
      if (field === 'name') setName(transcript.trim());
      else if (field === 'mobile') setMobile(extractMobileDigits(transcript));
      else if (field === 'age') setAge(extractAge(transcript));
      else if (field === 'reasonCustom') setReasonCustom(transcript.trim());
    } catch { setMicErr(t(lang, 'micError')); }
    setMicProcessingField(null);
  };

  const MicButton = ({ field }: { field: MicField }) => {
    const isActive = micField === field;
    const isProcessing = micProcessingField === field;
    return (
      <button type="button" onClick={() => (isActive ? stopMic() : startMic(field))} disabled={isProcessing}
        title={t(lang, isActive ? 'micListening' : 'micTap')}
        style={{
          width:56, height:56, borderRadius:'50%', border:'none', flexShrink:0, cursor: isProcessing ? 'wait' : 'pointer', fontSize:24,
          display:'flex', alignItems:'center', justifyContent:'center',
          background: isActive ? '#DC2626' : 'rgba(255,255,255,.18)', color:'#fff',
          animation: isActive ? 'mic-pulse 1s ease-in-out infinite' : 'none',
        }}>
        {isProcessing ? '⏳' : isActive ? '⏹' : '🎤'}
      </button>
    );
  };

  // ── Step 5: duplicate detection + intake save ──────────────────────
  // Reuses createIntakeSession/updateIntakeSession and kioskVerifyPatient
  // exactly as before — the only change is that the fields being saved now
  // come from confirmed structured input instead of one raw AI-parsed
  // transcript, so parsed_data can be built directly rather than inferred.
  // Saves the intake, then offers the optional Documents step — the intake
  // has to exist first, since that's what the scan-to-upload QR is bound to
  // (there's no patient record yet). The duplicate check runs after it
  // (checkMatch below).
  const submit = async () => {
    if (!reason) return;
    setStage('saving');
    const reasonEn = reason === 'other' ? reasonCustom.trim() : REASON_LABEL_EN[reason];
    const reasonNative = reason === 'other' ? reasonCustom.trim() : t(lang, `reason${reason[0]!.toUpperCase()}${reason.slice(1)}` as StringKey);
    const transcript = `${name}. ${mobile}. ${age}. ${reasonNative}`;

    try {
      const session = await createIntakeSession({ mode:'kiosk', language_code: lang, audio_duration_sec: null });
      await updateIntakeSession(session.id, {
        transcript,
        parsed_data: { name, mobile, gender:'', age, address:'', complaints: reasonEn, preferred_date:'' },
      });
      setReference(session.id);

      // Best-effort English translation of a freeform "Other" reason, for
      // reception's review list — same non-blocking pattern the old flow
      // used, never something the patient waits on.
      if (reason === 'other' && lang !== 'en-IN' && reasonCustom.trim()) {
        (async () => {
          try {
            const trRes = await authFetch('/api/voice/translate', {
              method:'POST', headers:{'Content-Type':'application/json'},
              body: JSON.stringify({ text: reasonCustom.trim(), source_language_code: lang, target_language_code:'en-IN' }),
            });
            const trData = await trRes.json();
            if (trRes.ok) await updateIntakeSession(session.id, { translated_text: trData.translated_text, parsed_data: { name, mobile, gender:'', age, address:'', complaints: trData.translated_text, preferred_date:'' } });
          } catch { /* reception can still work from the native-language text */ }
        })();
      }

      setStage('documents');
    } catch (e) {
      setErrorMsg(t(lang, 'errorGeneric'));
      setStage('error');
      resetTimerRef.current = setTimeout(goStart, RESET_AFTER_MS);
    }
  };

  const checkMatch = async () => {
    setShowUploadQR(false);
    setStage('checking-match');
    const result = await kioskVerifyPatient(name, mobile).catch(() => ({ matched:false }));
    if (result.matched) {
      setMatchResult(result);
      setStage('confirm-identity');
    } else {
      setStage('done');
      resetTimerRef.current = setTimeout(goStart, RESET_AFTER_MS);
    }
  };

  // Stable identity — MobileUploadQRModal's polling effect depends on it.
  const onDocsReceived = useCallback((status: MobileSessionStatus) => { setDocsReceived(n => Math.max(n, status.uploadCount)); }, []);

  const confirmIdentityYes = () => { setBookingOffer(matchResult); setStage('booking-offer'); };
  const confirmIdentityNo = () => { setStage('done'); resetTimerRef.current = setTimeout(goStart, RESET_AFTER_MS); };

  // ── Self-service appointment booking (unchanged business logic) ────
  const declineBooking = () => { setStage('done'); resetTimerRef.current = setTimeout(goStart, RESET_AFTER_MS); };

  const acceptBooking = async () => {
    setStage('booking-doctor');
    try { setBookingDoctors(await getKioskBookingDoctors()); }
    catch { setBookingDoctors([]); }
  };

  const selectBookingDoctor = async (doctor: BookingDoctor) => {
    setBookingSelectedDoctor(doctor);
    setBookingSlots(null); setBookingError('');
    setStage('booking-slot');
    try { setBookingSlots(await getKioskBookingSlots(doctor.id)); }
    catch { setBookingSlots([]); }
  };

  const confirmBookingSlot = async (slot: BookingSlot) => {
    if (!bookingOffer?.patient) return;
    setBookingError('');
    try {
      // Passing the intake lets the backend attach anything uploaded in the
      // Documents step to this (just verified) patient — booking closes the
      // intake below, so reception would otherwise never convert it.
      await kioskBookSlot(bookingOffer.patient.id, slot.id, reference);
      setBookingConfirmed(slot);
      setStage('booking-confirm');
      resetTimerRef.current = setTimeout(goStart, RESET_AFTER_MS);
      // The intake session this kiosk visit created (see submit()) exists to
      // get an unmatched/undecided patient in front of reception — but a
      // matched patient who just self-service-booked a future appointment
      // has nothing left for reception to action today, so it shouldn't
      // keep sitting in the open-intakes list forever. Best-effort: a failed
      // cleanup shouldn't undo an already-successful booking.
      if (reference) updateIntakeSession(reference, { status: 'booked' }).catch(() => {});
    } catch (e) {
      setBookingError((e instanceof Error && e.message) || 'Could not book this slot. Please try another.');
    }
  };

  // Normal document flow (no absolute-positioned header) inside a scrollable
  // outer container — a fixed/absolute header combined with vertically
  // centered content used to collide or clip whenever the viewport was short
  // (small phones, or a short desktop window), with no way to scroll to the
  // hidden part. Centering now happens via minHeight:100% on the inner flex
  // column, which still centers short content but lets tall content scroll.
  const shell = (children: ReactNode) => (
    <div style={{
      position:'fixed', inset:0, overflowY:'auto', WebkitOverflowScrolling:'touch',
      background:'linear-gradient(160deg,var(--teal, #0B5FD6),var(--accent, #00D9A5))',
      color:'#fff', fontFamily:'inherit', userSelect:'none',
    }}>
      <div style={{
        minHeight:'100%', boxSizing:'border-box', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        padding:'clamp(16px,5vw,40px)', textAlign:'center',
      }}>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', marginBottom:'clamp(16px,4vw,32px)' }}>
          {branding.logo_url
            ? <img src={branding.logo_url} alt="logo" style={{ height:48, objectFit:'contain', marginBottom:8 }} />
            : <img src={DEFAULT_ICON_URL} alt="logo" style={{ height:56, width:56, objectFit:'contain', marginBottom:8, background:'#fff', borderRadius:14, padding:4 }} />}
          <div style={{ fontSize:20, fontWeight:700 }}>{branding.hospital_name || 'Hospital'}</div>
          <div style={{ fontSize:12, letterSpacing:'1.5px', opacity:.8, textTransform:'uppercase' }}>Self-service intake kiosk</div>
        </div>
        {children}
      </div>
      <style>{`
        @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:.4 } }
        @keyframes mic-pulse { 0%,100% { opacity:1 } 50% { opacity:.55 } }
      `}</style>
    </div>
  );

  const BackButton = ({ onClick }: { onClick: () => void }) => (
    <button className="btn-ghost" style={{ marginTop:28, color:'#fff', borderColor:'rgba(255,255,255,.4)' }} onClick={onClick}>← {t(lang,'back')}</button>
  );
  const ContinueButton = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => (
    <button onClick={onClick} disabled={disabled}
      style={{
        padding:'20px 56px', fontSize:19, fontWeight:700, borderRadius:100, cursor: disabled ? 'default' : 'pointer',
        border:'none', background: disabled ? 'rgba(255,255,255,.25)' : '#fff', color: disabled ? 'rgba(255,255,255,.7)' : 'var(--teal, #0B5FD6)',
        boxShadow: disabled ? 'none' : '0 8px 24px rgba(0,0,0,.2)',
      }}>
      {t(lang,'next')} →
    </button>
  );
  const MicStatus = () => (micField || micProcessingField || micErr) ? (
    <div style={{ marginTop:10, fontSize:13, opacity:.9 }}>
      {micField ? t(lang,'micListening') : micProcessingField ? t(lang,'micProcessing') : <span style={{ color:'#FCA5A5' }}>⚠ {micErr}</span>}
    </div>
  ) : null;

  if (stage === 'checking') return shell(<div style={{ fontSize:16, opacity:.85 }}>Loading…</div>);

  if (stage === 'denied') {
    return shell(
      <>
        <div style={{ fontSize:48, marginBottom:20 }}>🔒</div>
        <div style={{ fontSize:22, fontWeight:800, marginBottom:12, maxWidth:480 }}>This kiosk link is invalid or has expired</div>
        <div style={{ fontSize:15, opacity:.85, maxWidth:440 }}>Please ask reception for a fresh kiosk link.</div>
      </>
    );
  }

  // ── Step 1: Language ────────────────────────────────────────────────
  if (stage === 'language') {
    return shell(
      <>
        <div style={{ fontSize:24, fontWeight:700, marginBottom:28 }}>Choose your language</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, maxWidth:560 }}>
          {LANGUAGES.map(l => (
            <button key={l.code} onClick={() => { setLang(l.code); setStage('name'); }}
              style={{ padding:'26px 16px', fontSize:20, fontWeight:700, background:'rgba(255,255,255,.12)', color:'#fff', border:'2px solid rgba(255,255,255,.4)', borderRadius:20, cursor:'pointer' }}>
              <div style={{ fontSize:30, marginBottom:6 }}>{l.flag}</div>
              {l.label}
            </button>
          ))}
        </div>
      </>
    );
  }

  // ── Step 2: Name ─────────────────────────────────────────────────────
  if (stage === 'name') {
    return shell(
      <>
        <div style={{ fontSize:28, fontWeight:800, marginBottom:28, maxWidth:560 }}>{t(lang,'nameTitle')}</div>
        <div style={{ display:'flex', alignItems:'center', gap:14, width:'100%', maxWidth:480, marginBottom:8 }}>
          <input autoFocus type="text" lang={lang.split('-')[0]} placeholder={t(lang,'namePlaceholder')} value={name}
            onChange={e => setName(e.target.value)}
            style={{ flex:1, padding:'18px 20px', fontSize:22, borderRadius:16, border:'none', textAlign:'center' }} />
          <MicButton field="name" />
        </div>
        <MicStatus />
        <div style={{ marginTop:32, display:'flex', gap:14 }}>
          <ContinueButton disabled={!name.trim()} onClick={() => setStage('mobile')} />
        </div>
        <BackButton onClick={() => setStage('language')} />
      </>
    );
  }

  // ── Step 3: Mobile ───────────────────────────────────────────────────
  if (stage === 'mobile') {
    const grouped = mobile.match(/.{1,5}/g)?.join('  ');
    const valid = mobile.length === 10;
    const pressDigit = (d: string) => setMobile(m => (m.length < 10 ? m + d : m));
    const backspace = () => setMobile(m => m.slice(0, -1));
    return shell(
      <>
        <div style={{ fontSize:28, fontWeight:800, marginBottom:20, maxWidth:560 }}>{t(lang,'mobileTitle')}</div>

        <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', justifyContent:'center', gap:14, marginBottom:6 }}>
          <div style={{
            fontSize:'clamp(18px,5.5vw,32px)', fontWeight:800, letterSpacing:'3px', fontFamily:'monospace',
            width:'fit-content', maxWidth:'92vw', whiteSpace:'nowrap', boxSizing:'border-box',
            padding:'10px 18px', borderRadius:14, background:'rgba(255,255,255,.12)',
            color: grouped ? '#fff' : 'rgba(255,255,255,.6)',
          }}>
            {grouped || t(lang,'mobilePlaceholder')}
          </div>
          <MicButton field="mobile" />
        </div>
        {mobile && !valid && <div style={{ fontSize:13, color:'#FDE68A', marginBottom:6 }}>{mobile.length}/10 — {t(lang,'mobileHintInvalid')}</div>}
        <MicStatus />

        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,clamp(56px,18vw,84px))', gap:'clamp(8px,2.5vw,12px)', marginTop:22, marginBottom:8 }}>
          {['1','2','3','4','5','6','7','8','9'].map(d => (
            <button key={d} onClick={() => pressDigit(d)}
              style={{ width:'clamp(56px,18vw,84px)', height:'clamp(56px,18vw,84px)', borderRadius:20, fontSize:'clamp(20px,5vw,28px)', fontWeight:700, background:'rgba(255,255,255,.14)', color:'#fff', border:'1px solid rgba(255,255,255,.3)', cursor:'pointer' }}>
              {d}
            </button>
          ))}
          <button onClick={() => setMobile('')} style={{ width:'clamp(56px,18vw,84px)', height:'clamp(56px,18vw,84px)', borderRadius:20, fontSize:'clamp(14px,4vw,16px)', fontWeight:700, background:'rgba(255,255,255,.08)', color:'#fff', border:'1px solid rgba(255,255,255,.2)', cursor:'pointer' }}>C</button>
          <button onClick={() => pressDigit('0')} style={{ width:'clamp(56px,18vw,84px)', height:'clamp(56px,18vw,84px)', borderRadius:20, fontSize:'clamp(20px,5vw,28px)', fontWeight:700, background:'rgba(255,255,255,.14)', color:'#fff', border:'1px solid rgba(255,255,255,.3)', cursor:'pointer' }}>0</button>
          <button onClick={backspace} style={{ width:'clamp(56px,18vw,84px)', height:'clamp(56px,18vw,84px)', borderRadius:20, fontSize:'clamp(18px,4.5vw,24px)', fontWeight:700, background:'rgba(255,255,255,.08)', color:'#fff', border:'1px solid rgba(255,255,255,.2)', cursor:'pointer' }}>⌫</button>
        </div>

        <div style={{ marginTop:22 }}>
          <ContinueButton disabled={!valid} onClick={() => setStage('age')} />
        </div>
        <BackButton onClick={() => setStage('name')} />
      </>
    );
  }

  // ── Step 3b: Age ─────────────────────────────────────────────────────
  // Same keypad as Mobile — a free-text field would pop the tablet's full
  // on-screen keyboard for what's only ever 1–3 digits.
  if (stage === 'age') {
    const valid = isValidAge(age);
    const pressDigit = (d: string) => setAge(a => (a.length < 3 ? (a === '0' ? d : a + d) : a));
    const key = (label: string, onClick: () => void, muted = false) => (
      <button key={label} onClick={onClick}
        style={{ width:'clamp(56px,18vw,84px)', height:'clamp(56px,18vw,84px)', borderRadius:20, fontSize:'clamp(20px,5vw,28px)', fontWeight:700, background: muted ? 'rgba(255,255,255,.08)' : 'rgba(255,255,255,.14)', color:'#fff', border:`1px solid rgba(255,255,255,${muted ? '.2' : '.3'})`, cursor:'pointer' }}>
        {label}
      </button>
    );
    return shell(
      <>
        <div style={{ fontSize:28, fontWeight:800, marginBottom:20, maxWidth:560 }}>{t(lang,'ageTitle')}</div>

        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:14, marginBottom:6 }}>
          <div style={{
            fontSize:'clamp(18px,5.5vw,32px)', fontWeight:800, fontFamily:'monospace', minWidth:120,
            padding:'10px 18px', borderRadius:14, background:'rgba(255,255,255,.12)',
            color: age ? '#fff' : 'rgba(255,255,255,.6)',
          }}>
            {age || t(lang,'agePlaceholder')}
          </div>
          <MicButton field="age" />
        </div>
        {age && !valid && <div style={{ fontSize:13, color:'#FDE68A', marginBottom:6 }}>{t(lang,'ageHintInvalid')}</div>}
        <MicStatus />

        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,clamp(56px,18vw,84px))', gap:'clamp(8px,2.5vw,12px)', marginTop:22, marginBottom:8 }}>
          {['1','2','3','4','5','6','7','8','9'].map(d => key(d, () => pressDigit(d)))}
          {key('C', () => setAge(''), true)}
          {key('0', () => pressDigit('0'))}
          {key('⌫', () => setAge(a => a.slice(0, -1)), true)}
        </div>

        <div style={{ marginTop:22 }}>
          <ContinueButton disabled={!valid} onClick={() => setStage('reason')} />
        </div>
        <BackButton onClick={() => setStage('mobile')} />
      </>
    );
  }

  // ── Step 4: Reason for visit ─────────────────────────────────────────
  if (stage === 'reason') {
    const canContinue = reason && (reason !== 'other' || reasonCustom.trim());
    return shell(
      <>
        <div style={{ fontSize:28, fontWeight:800, marginBottom:24, maxWidth:560 }}>{t(lang,'reasonTitle')}</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px,1fr))', gap:14, maxWidth:600, width:'100%' }}>
          {REASONS.map(id => (
            <button key={id} onClick={() => setReason(id)}
              style={{
                padding:'20px 14px', fontSize:16, fontWeight:700, borderRadius:18, cursor:'pointer',
                background: reason===id ? '#fff' : 'rgba(255,255,255,.12)', color: reason===id ? 'var(--teal, #0B5FD6)' : '#fff',
                border: `2px solid ${reason===id ? '#fff' : 'rgba(255,255,255,.4)'}`,
              }}>
              <div style={{ fontSize:26, marginBottom:6 }}>{REASON_ICON[id]}</div>
              {t(lang, `reason${id[0]!.toUpperCase()}${id.slice(1)}` as StringKey)}
            </button>
          ))}
        </div>

        {reason === 'other' && (
          <div style={{ display:'flex', alignItems:'center', gap:14, width:'100%', maxWidth:480, marginTop:20 }}>
            <input autoFocus type="text" lang={lang.split('-')[0]} placeholder={t(lang,'reasonOtherPlaceholder')} value={reasonCustom}
              onChange={e => setReasonCustom(e.target.value)}
              style={{ flex:1, padding:'16px 18px', fontSize:18, borderRadius:14, border:'none', textAlign:'center' }} />
            <MicButton field="reasonCustom" />
          </div>
        )}
        <MicStatus />

        <div style={{ marginTop:26 }}>
          <ContinueButton disabled={!canContinue} onClick={submit} />
        </div>
        <BackButton onClick={() => setStage('age')} />
      </>
    );
  }

  if (stage === 'saving') {
    return shell(<div style={{ fontSize:48, animation:'pulse 1.2s infinite' }}>⏳</div>);
  }

  // ── Step 4b: Documents (optional) ───────────────────────────────────
  // The intake is already saved at this point, so there's no Back — the
  // patient either uploads from their phone via the QR or skips. Uploads
  // wait on the intake for reception (Patient intake screen) and move onto
  // the patient's record once registered.
  if (stage === 'documents' && reference) {
    return shell(
      <>
        <div style={{ fontSize:56, marginBottom:16 }}>📄</div>
        <div style={{ fontSize:28, fontWeight:800, marginBottom:12, maxWidth:560 }}>{t(lang,'docsTitle')}</div>
        <div style={{ fontSize:15, opacity:.9, marginBottom:28, maxWidth:480 }}>{t(lang,'docsSubtitle')}</div>

        <button onClick={() => setShowUploadQR(true)}
          style={{ padding:'20px 40px', fontSize:18, fontWeight:700, background:'rgba(255,255,255,.14)', color:'#fff', border:'2px solid rgba(255,255,255,.5)', borderRadius:20, cursor:'pointer' }}>
          📱 {t(lang,'docsScan')}
        </button>

        {docsReceived > 0 && (
          <div style={{ marginTop:18, padding:'8px 18px', borderRadius:100, background:'rgba(255,255,255,.18)', fontSize:15, fontWeight:700 }}>
            ✓ {docsReceived} {t(lang,'docsReceived')}
          </div>
        )}

        <div style={{ marginTop:32 }}>
          {docsReceived > 0
            ? <ContinueButton onClick={checkMatch} />
            : <button className="btn-ghost" style={{ padding:'16px 44px', fontSize:17, color:'#fff', borderColor:'rgba(255,255,255,.5)', background:'transparent' }} onClick={checkMatch}>
                {t(lang,'docsSkip')} →
              </button>}
        </div>

        {/* text-ink: the kiosk shell sets white text, which the modal's
            white card would otherwise inherit for its unstyled title. */}
        {showUploadQR && (
          <div className="text-ink" style={{ userSelect:'text' }}>
          <MobileUploadQRModal
            intakeId={reference}
            category="Other"
            title={t(lang,'docsScan')}
            hint={t(lang,'docsQrHint')}
            onReceived={onDocsReceived}
            onClose={() => setShowUploadQR(false)}
          />
          </div>
        )}
      </>
    );
  }

  if (stage === 'checking-match') {
    return shell(
      <>
        <div style={{ fontSize:48, marginBottom:20, animation:'pulse 1.2s infinite' }}>⏳</div>
        <div style={{ fontSize:20, fontWeight:700 }}>{t(lang,'checkingRecords')}</div>
      </>
    );
  }

  // ── Step 5: duplicate-detection result ──────────────────────────────
  if (stage === 'confirm-identity') {
    return shell(
      <>
        <div style={{ fontSize:56, marginBottom:16 }}>🙋</div>
        <div style={{ fontSize:26, fontWeight:800, marginBottom:20 }}>{t(lang,'isThisYou')}</div>
        <div style={{ background:'rgba(255,255,255,.1)', border:'2px solid rgba(255,255,255,.3)', borderRadius:18, padding:'18px 28px', marginBottom:28, minWidth:260 }}>
          <div style={{ fontSize:22, fontWeight:700 }}>{matchResult?.patient?.name}</div>
          <div style={{ fontSize:13, opacity:.75, marginTop:4, fontFamily:'monospace' }}>{matchResult?.patient?.uhid}</div>
        </div>
        <div style={{ display:'flex', gap:16, flexWrap:'wrap', justifyContent:'center' }}>
          <button onClick={confirmIdentityYes}
            style={{ padding:'18px 40px', fontSize:18, fontWeight:700, background:'#fff', color:'var(--teal, #0B5FD6)', border:'none', borderRadius:100, cursor:'pointer', boxShadow:'0 8px 24px rgba(0,0,0,.2)' }}>
            ✓ {t(lang,'yesThatsMe')}
          </button>
          <button onClick={confirmIdentityNo} className="btn-ghost"
            style={{ padding:'18px 34px', fontSize:16, color:'#fff', borderColor:'rgba(255,255,255,.4)', background:'transparent' }}>
            {t(lang,'noSomeoneElse')}
          </button>
        </div>
      </>
    );
  }

  if (stage === 'done') {
    return shell(
      <>
        <div style={{ fontSize:64, marginBottom:20 }}>✅</div>
        <div style={{ fontSize:26, fontWeight:800, marginBottom:12 }}>{t(lang,'thankYou')}</div>
        <div style={{ fontSize:16, opacity:.9, marginBottom:20, maxWidth:440 }}>{t(lang,'doneMessage')}</div>
        <div style={{ fontSize:13, opacity:.7, fontFamily:'monospace' }}>{t(lang,'reference')}: INTAKE-{reference}</div>
      </>
    );
  }

  // ── Self-service appointment booking screens (unchanged) ────────────
  if (stage === 'booking-offer' && bookingOffer?.patient) {
    return shell(
      <>
        <div style={{ fontSize:56, marginBottom:20 }}>📅</div>
        <div style={{ fontSize:26, fontWeight:800, marginBottom:12, maxWidth:500 }}>Since you're here, {bookingOffer.patient.name} — book your next appointment?</div>
        {bookingOffer.suggested_doctor_name && (
          <div style={{ fontSize:15, opacity:.85, marginBottom:28 }}>Last time you saw {bookingOffer.suggested_doctor_name}</div>
        )}
        <div style={{ display:'flex', gap:16, marginTop:8 }}>
          <button onClick={acceptBooking}
            style={{ padding:'20px 44px', fontSize:19, fontWeight:700, background:'#fff', color:'var(--teal, #0B5FD6)', border:'none', borderRadius:100, cursor:'pointer', boxShadow:'0 8px 24px rgba(0,0,0,.2)' }}>
            ✓ Yes, book now
          </button>
          <button onClick={declineBooking} className="btn-ghost"
            style={{ padding:'20px 36px', fontSize:17, color:'#fff', borderColor:'rgba(255,255,255,.4)', background:'transparent' }}>
            Not now
          </button>
        </div>
      </>
    );
  }

  if (stage === 'booking-doctor') {
    return shell(
      <>
        <div style={{ fontSize:24, fontWeight:700, marginBottom:8 }}>Choose a doctor</div>
        <div style={{ fontSize:14, opacity:.8, marginBottom:24 }}>Tap a doctor to see available times</div>
        {bookingDoctors.length === 0 ? (
          <div style={{ fontSize:15, opacity:.85 }}>No doctors available for booking right now — please ask reception.</div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px,1fr))', gap:14, maxWidth:560, width:'100%' }}>
            {bookingDoctors.map(d => (
              <button key={d.id} onClick={() => selectBookingDoctor(d)}
                style={{
                  padding:'20px 16px', fontSize:16, fontWeight:700, borderRadius:16, cursor:'pointer',
                  background: d.id === bookingOffer?.suggested_doctor_id ? '#fff' : 'rgba(255,255,255,.12)',
                  color: d.id === bookingOffer?.suggested_doctor_id ? 'var(--teal, #0B5FD6)' : '#fff',
                  border:'2px solid rgba(255,255,255,.4)',
                }}>
                👨‍⚕️ {d.full_name}
                {d.specialty_name && <div style={{ fontSize:12, fontWeight:500, marginTop:4, opacity:.8 }}>{d.specialty_name}</div>}
                {d.id === bookingOffer?.suggested_doctor_id && <div style={{ fontSize:11, fontWeight:600, marginTop:6 }}>Previously consulted</div>}
              </button>
            ))}
          </div>
        )}
        <button className="btn-ghost" style={{ marginTop:32, color:'#fff', borderColor:'rgba(255,255,255,.4)' }} onClick={declineBooking}>← Back</button>
      </>
    );
  }

  if (stage === 'booking-slot') {
    const slotsByDate: Record<string, BookingSlot[]> = {};
    (bookingSlots || []).forEach(s => { (slotsByDate[s.date] ||= []).push(s); });
    return shell(
      <>
        <div style={{ fontSize:24, fontWeight:700, marginBottom:8 }}>Choose a time — {bookingSelectedDoctor?.full_name}</div>
        {bookingError && <div style={{ marginBottom:14, padding:'8px 16px', background:'rgba(220,38,38,.25)', border:'1px solid rgba(220,38,38,.5)', borderRadius:12, fontSize:13, maxWidth:480 }}>{bookingError}</div>}
        {bookingSlots === null ? (
          <div style={{ fontSize:15, opacity:.85 }}>Loading available times…</div>
        ) : bookingSlots.length === 0 ? (
          <div style={{ fontSize:15, opacity:.85 }}>No upcoming open slots for this doctor — please ask reception.</div>
        ) : (
          <div style={{ maxWidth:560, width:'100%', maxHeight:320, overflowY:'auto' }}>
            {Object.entries(slotsByDate).map(([date, daySlots]) => (
              <div key={date} style={{ marginBottom:16 }}>
                <div style={{ fontSize:13, fontWeight:700, opacity:.8, marginBottom:8, textAlign:'left' }}>
                  {new Date(date).toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'short' })}
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                  {daySlots.map(s => (
                    <button key={s.id} onClick={() => confirmBookingSlot(s)}
                      style={{ padding:'12px 18px', fontSize:15, fontWeight:700, background:'rgba(255,255,255,.15)', color:'#fff', border:'2px solid rgba(255,255,255,.4)', borderRadius:14, cursor:'pointer' }}>
                      {(s.start_time || '').slice(0,5)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <button className="btn-ghost" style={{ marginTop:24, color:'#fff', borderColor:'rgba(255,255,255,.4)' }} onClick={acceptBooking}>← Choose a different doctor</button>
      </>
    );
  }

  if (stage === 'booking-confirm') {
    return shell(
      <>
        <div style={{ fontSize:64, marginBottom:20 }}>✅</div>
        <div style={{ fontSize:26, fontWeight:800, marginBottom:12 }}>Appointment booked!</div>
        <div style={{ fontSize:16, opacity:.9, marginBottom:8, maxWidth:440 }}>
          {bookingSelectedDoctor?.full_name}, {bookingConfirmed && new Date(bookingConfirmed.date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })} at {(bookingConfirmed?.start_time || '').slice(0,5)}
        </div>
        <div style={{ fontSize:14, opacity:.8 }}>See you then — please take a seat now.</div>
      </>
    );
  }

  // error
  return shell(
    <>
      <div style={{ fontSize:48, marginBottom:20 }}>⚠</div>
      <div style={{ fontSize:20, fontWeight:700, marginBottom:12 }}>{errorMsg}</div>
      <button onClick={goStart}
        style={{ padding:'16px 40px', fontSize:16, fontWeight:700, background:'#fff', color:'var(--teal, #0B5FD6)', border:'none', borderRadius:100, cursor:'pointer', marginTop:12 }}>
        {t(lang,'startOver')}
      </button>
    </>
  );
}
